import axios from "axios";
import { Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { RiRobot2Fill } from "react-icons/ri";
import { useSmartNavigate } from "../../hooks/useSmartNavigate";

const GREETINGS = ["hi", "hello", "hey", "hiya", "howdy", "sup", "good morning", "good evening", "good afternoon", "namaste"];
const THANKS = ["thank", "thanks", "thank you", "thx", "ty", "great", "awesome", "nice", "cool", "perfect"];
const BYE = ["bye", "goodbye", "see you", "cya", "take care", "later"];

const TypingDots = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "4px", padding: "4px 2px" }}>
        {[0, 1, 2].map((i) => (
            <span
                key={i}
                style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "#ab310c",
                    opacity: 0.6,
                    animation: "bounce 1.2s infinite",
                    animationDelay: `${i * 0.2}s`,
                }}
            />
        ))}
    </div>
);

const ChatBot = () => {
    const [open, setOpen] = useState(false);
    const [coupons, setCoupons] = useState([]);
    const [messages, setMessages] = useState([
        { from: "bot", text: "Hi there! Welcome to Sakhi Salon. I'm your virtual assistant. How can I help you today?" },
    ]);
    const [input, setInput] = useState("");
    const [categories, setCategories] = useState([]);
    const [isTyping, setIsTyping] = useState(false);
    const [hasUnread, setHasUnread] = useState(true);
    const chatRef = useRef(null);
    const inputRef = useRef(null);
    const { goTo, goToService } = useSmartNavigate();

    const API = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axios.get(`${API}/service-category/get-all`);
                if (res.data?.success) setCategories(res.data.data);
            } catch (err) {
                console.error("Chatbot category fetch failed", err);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchCoupons = async () => {
            try {
                const res = await axios.get(`${API}/coupons`);
                setCoupons(res.data || []);
            } catch (err) {
                console.error("Coupon fetch failed", err);
            }
        };

        fetchCoupons();
    }, []);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [open]);

    useEffect(() => {
        chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, isTyping]);

    useEffect(() => {
        if (open) {
            setHasUnread(false);
            setTimeout(() => inputRef.current?.focus(), 300);
        }
    }, [open]);

    const getBotReply = async (message) => {
        const msg = message.toLowerCase().trim();

        // Greetings
        if (GREETINGS.some((g) => msg === g || msg.startsWith(g))) {
            return "Hello! Great to have you here at Sakhi Salon. You can ask me about our services, packages, bookings, blog, or anything else. What would you like to know?";
        }

        // Thanks
        if (THANKS.some((t) => msg.includes(t))) {
            return "You're most welcome! Is there anything else I can help you with?";
        }

        // Bye
        if (BYE.some((b) => msg.includes(b))) {
            return "Goodbye! Have a wonderful day. Visit us again anytime.";
        }

        // Offers / Coupons
        if (
            msg.includes("offer") ||
            msg.includes("coupon") ||
            msg.includes("discount") ||
            msg.includes("deal") ||
            msg.includes("promo")
        ) {
            if (!coupons.length) {
                return "Fetching latest offers for you... please wait a moment.";
            }

            const now = new Date();

            const activeCoupons = coupons.filter(
                (c) =>
                    c.isActive &&
                    new Date(c.startsAt) <= now &&
                    new Date(c.expiresAt) >= now
            );

            if (activeCoupons.length === 0) {
                return "Currently, there are no active offers. But stay tuned—great deals drop often!";
            }

            const formatted = activeCoupons
                .map((c) => {
                    const discount =
                        c.discountType === "FLAT"
                            ? `₹${c.discountValue} OFF`
                            : `${c.discountValue}% OFF`;

                    return `• ${c.code} → ${discount} (Min: ₹${c.minOrderAmount})`;
                })
                .join("\n");

            return `Here are the latest offers for you:\n\n${formatted}\n\nApply these during checkout to save more ✨`;
        }

        // Identity
        if (msg.includes("who are you") || msg.includes("what are you") || msg.includes("are you a bot") || msg.includes("are you human")) {
            return "I'm Sakhi Assistant, your virtual guide for Sakhi Salon. I can help you navigate our services, packages, bookings, and more!";
        }

        // Help
        if (msg.includes("help") || msg === "?") {
            return "Here's what I can help you with:\n- Browse our services or packages\n- Book an appointment\n- Check pricing\n- View your past bookings\n- Read our blog\n- Contact us\n- View your profile or cart\n\nJust ask away!";
        }

        // Home
        if (msg.includes("home") || msg.includes("main page") || msg.includes("go back")) {
            goTo("home");
            return "Taking you to the home page now.";
        }

        // About
        if (msg.includes("about") || msg.includes("about us") || msg.includes("who are sakhi") || msg.includes("your story")) {
            goTo("about");
            return "Let me take you to our About page where you can learn more about Sakhi Salon.";
        }

        // Services (list all)
        if (msg.includes("service") || msg.includes("category") || msg.includes("what do you offer") || msg.includes("what do you have") || msg.includes("treatments")) {
            if (categories.length === 0) return "We're loading our services list. Please try again in a moment.";
            const names = categories.map((c) => c.categoryName).join(", ");
            goTo("services");
            return `We offer a range of premium services including: ${names}.\n\nTaking you to our services page now. Type any service name to jump directly to it.`;
        }

        // Packages
        if (msg.includes("package") || msg.includes("combo") || msg.includes("bundle") || msg.includes("deal")) {
            goTo("packages");
            return "Taking you to our Packages page. You'll find our best value combos and bundled offers there.";
        }

        // Book appointment
        if (msg.includes("book") || msg.includes("appointment") || msg.includes("schedule") || msg.includes("reserve")) {
            goTo("services");
            return "Redirecting you to our services page where you can pick a service and book your appointment.";
        }

        // My bookings
        if (msg.includes("my booking") || msg.includes("my appointment") || msg.includes("past booking") || msg.includes("order history") || msg.includes("booking history")) {
            goTo("bookings");
            return "Taking you to your bookings page where you can see all your past and upcoming appointments.";
        }

        // Cart
        if (msg.includes("cart") || msg.includes("my cart") || msg.includes("service cart") || msg.includes("checkout")) {
            goTo("cart");
            return "Heading to your service cart now. You can review and confirm your selected services there.";
        }

        // Profile
        if (msg.includes("profile") || msg.includes("my account") || msg.includes("my details") || msg.includes("account settings") || msg.includes("edit profile")) {
            goTo("profile");
            return "Taking you to your profile page where you can update your details and preferences.";
        }

        // Blog
        if (msg.includes("blog") || msg.includes("article") || msg.includes("read") || msg.includes("tips") || msg.includes("news")) {
            goTo("blog");
            return "Taking you to our Blog page. We share beauty tips, trends, and salon news there.";
        }

        // Catalog
        if (msg.includes("catalog") || msg.includes("product") || msg.includes("shop") || msg.includes("store")) {
            goTo("catalog");
            return "Taking you to our product catalog. You can browse all available products there.";
        }

        // Contact
        if (msg.includes("contact") || msg.includes("reach") || msg.includes("call") || msg.includes("email") || msg.includes("support") || msg.includes("help desk")) {
            goTo("contact");
            return "Taking you to our Contact page. You can reach us via phone, email, or the contact form there.";
        }

        // Location / address
        if (msg.includes("location") || msg.includes("where") || msg.includes("address") || msg.includes("direction") || msg.includes("find you")) {
            goTo("contact");
            return "Our address and location map are available on the Contact page. Redirecting you there now.";
        }

        // Pricing
        if (msg.includes("price") || msg.includes("cost") || msg.includes("rate") || msg.includes("charge") || msg.includes("fee") || msg.includes("how much")) {
            goTo("services");
            return "Pricing varies by service. Taking you to our services page so you can explore all options with their rates.";
        }

        // Hygiene
        if (msg.includes("hygiene") || msg.includes("clean") || msg.includes("safe") || msg.includes("sanitize")) {
            return "Cleanliness is our top priority. All tools are sterilized before each use, and our salon maintains strict hygiene protocols to keep you safe and comfortable.";
        }

        // Staff
        if (msg.includes("professional") || msg.includes("staff") || msg.includes("expert") || msg.includes("team") || msg.includes("stylist")) {
            return "Our team consists of trained, certified professionals with years of experience. Each stylist specializes in their domain to ensure you get the best results.";
        }

        // Timing
        if (msg.includes("time") || msg.includes("hour") || msg.includes("open") || msg.includes("timing") || msg.includes("when")) {
            return "Our salon is open daily. For specific hours and slot availability, we recommend booking online to check real-time availability.";
        }

        // Cancel / reschedule
        if (msg.includes("cancel") || msg.includes("reschedule")) {
            goTo("bookings");
            return "You can manage your appointments from your bookings page. Taking you there now.";
        }

        // Login
        if (msg.includes("login") || msg.includes("sign in") || msg.includes("log in")) {
            goTo("login");
            return "Taking you to the login page now.";
        }

        // Register
        if (msg.includes("register") || msg.includes("sign up") || msg.includes("create account") || msg.includes("new account")) {
            goTo("register");
            return "Taking you to the registration page. Creating an account is quick and easy!";
        }

        // Dynamic category match
        const matchedCategory = categories.find((c) =>
            msg.includes(c.categoryName.toLowerCase())
        );
        if (matchedCategory) {
            goToService(matchedCategory._id);
            return `Taking you to our ${matchedCategory.categoryName} services now. You'll find all available options and pricing there.`;
        }

        // Fallback
        return "I'm not sure I understood that fully. You can ask me about services, packages, bookings, pricing, our team, or any page you'd like to visit. I'm here to help!";
    };

    const handleSend = async () => {
        if (!input.trim()) return;
        const userMessage = input.trim();
        setMessages((prev) => [...prev, { from: "user", text: userMessage }]);
        setInput("");
        setIsTyping(true);
        setTimeout(async () => {
            const reply = await getBotReply(userMessage);
            setIsTyping(false);
            setMessages((prev) => [...prev, { from: "bot", text: reply }]);
        }, 900);
    };

    return (
        <>
            <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0 rgba(171, 49, 12, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(171, 49, 12, 0); }
          100% { box-shadow: 0 0 0 0 rgba(171, 49, 12, 0); }
        }
        .chat-message {
          animation: fadeIn 0.3s ease forwards;
        }
        .chat-window {
          animation: slideUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          transform-origin: bottom right;
        }
        .fab-btn {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
        }
        .fab-btn:hover { transform: scale(1.08); }
        .fab-btn:active { transform: scale(0.94); }
        .send-btn {
          transition: transform 0.15s ease, background 0.2s ease;
        }
        .send-btn:hover { transform: scale(1.05); }
        .send-btn:active { transform: scale(0.92); }
        .input-field:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(171, 49, 12, 0.25);
        }
        .custom-scroll::-webkit-scrollbar { width: 4px; }
        .custom-scroll::-webkit-scrollbar-track { background: transparent; }
        .custom-scroll::-webkit-scrollbar-thumb { background: #e8c5ba; border-radius: 99px; }
      `}</style>

            <div className="right-3 sm:right-6" style={{ position: "fixed", bottom: "74px",  zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "12px" }}>

                {/* CHAT WINDOW */}
                {open && (
                    <div
                        className="chat-window w-[95%] sm:w-[360px] h-[400px] sm:h-[500px]"
                        style={{
                            background: "#fff",
                            borderRadius: "20px",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 4px 20px rgba(171,49,12,0.1)",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            border: "1px solid rgba(171,49,12,0.12)",
                        }}
                    >
                        {/* HEADER */}
                        <div
                            style={{
                                background: "linear-gradient(135deg, #9b2608, #c94118)",
                                padding: "10px 12px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "10px",
                                    background: "rgba(255,255,255,0.15)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backdropFilter: "blur(6px)",
                                    flexShrink: 0,
                                }}
                            >
                                <RiRobot2Fill size={17} color="#fff" />
                            </div>

                            <div style={{ flex: 1, lineHeight: 1.2 }}>
                                <p style={{ margin: 0, color: "#fff", fontWeight: 600, fontSize: "13px" }}>
                                    Sakhi Assistant
                                </p>
                                <div style={{ display: "flex", alignItems: "center", gap: "5px", marginTop: "2px" }}>
                                    <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#4ade80" }} />
                                    <p style={{ margin: 0, color: "rgba(255,255,255,0.7)", fontSize: "10px" }}>Online</p>
                                </div>
                            </div>

                            <button
                                onClick={() => setOpen(false)}
                                style={{
                                    background: "rgba(255,255,255,0.15)", border: "none", cursor: "pointer",
                                    borderRadius: "8px", width: "28px", height: "28px",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: "#fff", transition: "background 0.2s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {/* CHAT BODY */}
                        <div
                            ref={chatRef}
                            className="custom-scroll"
                            style={{
                                flex: 1, overflowY: "auto",
                                padding: "16px 14px",
                                display: "flex", flexDirection: "column", gap: "10px",
                                background: "linear-gradient(180deg, #fdf5f3 0%, #fff8f6 100%)",
                            }}
                        >
                            {messages.map((msg, i) => (
                                <div
                                    key={i}
                                    className="chat-message"
                                    style={{ display: "flex", justifyContent: msg.from === "bot" ? "flex-start" : "flex-end" }}
                                >
                                    {msg.from === "bot" && (
                                        <div style={{
                                            width: "26px", height: "26px", borderRadius: "8px",
                                            background: "linear-gradient(135deg, #ab310c, #d44a20)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            flexShrink: 0, marginRight: "8px", marginTop: "2px",
                                        }}>
                                            <RiRobot2Fill size={13} color="#fff" />
                                        </div>
                                    )}
                                    <div style={{
                                        maxWidth: "72%",
                                        padding: "10px 13px",
                                        borderRadius: msg.from === "bot" ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
                                        fontSize: "13px",
                                        lineHeight: "1.55",
                                        whiteSpace: "pre-line",
                                        wordBreak: "break-word",
                                        background: msg.from === "bot"
                                            ? "#fff"
                                            : "linear-gradient(135deg, #ab310c, #c94118)",
                                        color: msg.from === "bot" ? "#374151" : "#fff",
                                        boxShadow: msg.from === "bot"
                                            ? "0 1px 6px rgba(0,0,0,0.07)"
                                            : "0 2px 10px rgba(171,49,12,0.35)",
                                        border: msg.from === "bot" ? "1px solid rgba(171,49,12,0.08)" : "none",
                                    }}>
                                        {msg.text}
                                    </div>
                                </div>
                            ))}

                            {isTyping && (
                                <div className="chat-message" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <div style={{
                                        width: "26px", height: "26px", borderRadius: "8px",
                                        background: "linear-gradient(135deg, #ab310c, #d44a20)",
                                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                    }}>
                                        <RiRobot2Fill size={13} color="#fff" />
                                    </div>
                                    <div style={{
                                        background: "#fff", border: "1px solid rgba(171,49,12,0.08)",
                                        borderRadius: "4px 16px 16px 16px",
                                        padding: "8px 14px",
                                        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
                                    }}>
                                        <TypingDots />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* QUICK SUGGESTIONS */}
                        <div
                            className="flex max-w-[90%] overflow-x-auto gap-2 px-3 pt-2 pb-2 no-scrollbar"
                            style={{
                                WebkitOverflowScrolling: "touch",
                                padding: "8px 14px 0",
                                background: "#fff",
                                borderTop: "1px solid rgba(171,49,12,0.06)",
                                display: "flex", gap: "6px",
                            }}>
                            {["Help", "Services", "Packages", "Offers", "Contact"].map((s) => (
                                <button
                                    key={s}
                                    onClick={async () => {
                                        setTimeout(() => {
                                            setMessages((prev) => [...prev, { from: "user", text: s }]);
                                            setIsTyping(true);
                                            setTimeout(async () => {
                                                const reply = await getBotReply(s);
                                                setIsTyping(false);
                                                setMessages((prev) => [...prev, { from: "bot", text: reply }]);
                                            }, 900);
                                        }, 50);
                                    }}
                                    className="shrink-0 whitespace-nowrap"
                                    style={{
                                        padding: "4px 10px", fontSize: "11px", fontWeight: 500,
                                        borderRadius: "99px", border: "1px solid rgba(171,49,12,0.25)",
                                        background: "transparent", color: "#ab310c", cursor: "pointer",
                                        transition: "all 0.15s",
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = "#ab310c"; e.currentTarget.style.color = "#fff"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#ab310c"; }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>

                        {/* INPUT */}
                        <div style={{
                            padding: "10px 12px 12px",
                            background: "#fff",
                            display: "flex", alignItems: "center", gap: "8px",
                        }}>
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                                placeholder="Ask about services, booking..."
                                className="input-field"
                                style={{
                                    flex: 1, padding: "10px 14px", fontSize: "13px",
                                    border: "1.5px solid #e5d5d0", borderRadius: "12px",
                                    background: "#fdf5f3", color: "#374151",
                                    transition: "border-color 0.2s, box-shadow 0.2s",
                                }}
                                onFocus={(e) => e.currentTarget.style.borderColor = "#ab310c"}
                                onBlur={(e) => e.currentTarget.style.borderColor = "#e5d5d0"}
                            />
                            <button
                                onClick={handleSend}
                                className="send-btn"
                                style={{
                                    width: "38px", height: "38px", flexShrink: 0,
                                    background: input.trim() ? "linear-gradient(135deg, #ab310c, #c94118)" : "#e5d5d0",
                                    border: "none", borderRadius: "11px", cursor: "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    transition: "background 0.2s, transform 0.15s",
                                }}
                            >
                                <Send size={15} color={input.trim() ? "#fff" : "#a8887e"} />
                            </button>
                        </div>
                    </div>
                )}

                {/* FAB BUTTON */}
                <button
                    className="fab-btn"
                    onClick={() => setOpen((prev) => !prev)}
                    style={{
                        width: "56px", height: "56px", borderRadius: "16px",
                        background: open
                            ? "linear-gradient(135deg, #7a1a04, #ab310c)"
                            : "linear-gradient(135deg, #ab310c, #d44a20)",
                        border: "none", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 8px 24px rgba(171,49,12,0.4)",
                        animation: !open && hasUnread ? "pulse-ring 2s infinite" : "none",
                        position: "relative",
                    }}
                >
                    {open ? (
                        <X size={22} color="#fff" />
                    ) : (
                        <RiRobot2Fill size={26} color="#fff" />
                    )}
                    {hasUnread && !open && (
                        <span style={{
                            position: "absolute", top: "-4px", right: "-4px",
                            width: "12px", height: "12px", borderRadius: "50%",
                            background: "#4ade80", border: "2px solid #fff",
                        }} />
                    )}
                </button>
            </div>
        </>
    );
};

export default ChatBot;